import React, { useContext, useState, useEffect } from 'react';
import { SessionContext } from '../../contexts/SessionContext';
import { useWhitelabel } from '../../contexts/WhitelabelThemeContext';

// Whitelabel configuration error component
export const WhitelabelConfigurationError = () => {
  const prefersDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: prefersDarkMode ? '#121212' : '#ffffff', // Adapts to dark/light mode
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'opacity 0.3s ease-in-out',
        opacity: 1,
      }}
    >
      {/* Background gradient effect */}
      <div 
        style={{
          position: 'absolute',
          top: '30%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '300px',
          height: '300px',
          background: prefersDarkMode 
            ? 'radial-gradient(circle, rgba(40,40,40,0.5) 0%, rgba(18,18,18,0) 70%)'
            : 'radial-gradient(circle, rgba(240,240,240,1) 0%, rgba(255,255,255,0) 70%)',
          opacity: 0.8,
          animation: 'pulse 2s infinite ease-in-out',
        }}
      />
      <h1>Configuration error</h1>
      <br />
      <p>If your are the owner of the app, please make sure to set the correct settings.</p>
    </div>
  );
};

// Fullscreen loading component for whitelabel loading
export const WhitelabelLoadingScreen = () => {
  // Detect dark mode preference
  const prefersDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  
  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: prefersDarkMode ? '#121212' : '#ffffff', // Adapts to dark/light mode
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'opacity 0.3s ease-in-out',
        opacity: 1,
      }}
    >
      {/* Background gradient effect */}
      <div 
        style={{
          position: 'absolute',
          top: '30%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '300px',
          height: '300px',
          background: prefersDarkMode 
            ? 'radial-gradient(circle, rgba(40,40,40,0.5) 0%, rgba(18,18,18,0) 70%)'
            : 'radial-gradient(circle, rgba(240,240,240,1) 0%, rgba(255,255,255,0) 70%)',
          opacity: 0.8,
          animation: 'pulse 2s infinite ease-in-out',
        }}
      />
      
      {/* Spinner */}
      <div 
        style={{
          width: 60,
          height: 60,
          borderRadius: '50%',
          border: `3px solid ${prefersDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          borderTopColor: prefersDarkMode ? '#cccccc' : '#555555',
          animation: 'spin 1s linear infinite',
          position: 'relative',
        }}
      />
      
      {/* Loading text */}
      <p style={{ 
        marginTop: '20px', 
        color: prefersDarkMode ? '#aaaaaa' : '#666666',
        fontFamily: 'ui-sans-serif, system-ui, -apple-system, sans-serif',
        fontSize: '14px',
        fontWeight: 500,
        letterSpacing: '0.5px',
        position: 'relative',
        animation: 'fadeInOut 2s infinite ease-in-out',
      }}>
        Loading experience...
      </p>
      
      {/* Animations */}
      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          
          @keyframes pulse {
            0% { transform: translate(-50%, -50%) scale(1); opacity: 0.6; }
            50% { transform: translate(-50%, -50%) scale(1.05); opacity: 0.8; }
            100% { transform: translate(-50%, -50%) scale(1); opacity: 0.6; }
          }
          
          @keyframes fadeInOut {
            0% { opacity: 0.7; }
            50% { opacity: 1; }
            100% { opacity: 0.7; }
          }
        `}
      </style>
    </div>
  );
};

// Beautiful commercial no-permission component
export const WhitelabelNoPermission = ({ delay = 750 } = {}) => {
  const { theme } = useWhitelabel();
  const { actions } = useContext(SessionContext);
  const prefersDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay]);

  const handleContact = () => {
    if (theme.contactEmail) {
      window.location.href = `mailto:${theme.contactEmail}?subject=Access Request for ${theme.name}&body=Hello,%0D%0A%0D%0AI would like to request access to ${theme.name}. Please let me know about available plans and pricing.%0D%0A%0D%0AThank you!`;
    }
  };

  const handleLogout = async () => {
    await actions.logout();
  };

  // Helper function to convert any color format to rgba
  const colorToRgba = (color, alpha) => {
    // If it's already rgba, extract the rgb values
    if (color.includes('rgba') || color.includes('rgb')) {
      const match = color.match(/\d+/g);
      if (match && match.length >= 3) {
        return `rgba(${match[0]}, ${match[1]}, ${match[2]}, ${alpha})`;
      }
    }
    // If it's hex
    if (color.includes('#')) {
      const hex = color.replace('#', '');
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    // Fallback
    return `rgba(3, 202, 230, ${alpha})`;
  };

  // Get primary color from theme
  const primaryColor = theme.colors?.primary || 'rgba(3, 202, 230, 1)';

  // Don't render anything until the delay has passed
  if (!isVisible) {
    return null;
  }

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `linear-gradient(135deg, ${colorToRgba(primaryColor, 0.9)} 0%, ${colorToRgba(primaryColor, 0.95)} 50%, ${colorToRgba(primaryColor, 1)} 100%)`,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: theme.fonts?.body || 'ui-sans-serif, system-ui, -apple-system, sans-serif',
        overflow: 'hidden',
        animation: 'fadeIn 0.5s ease-out',
      }}
    >
      {/* Animated background elements */}
      <div 
        style={{
          position: 'absolute',
          top: '-50%',
          left: '-50%',
          width: '200%',
          height: '200%',
          background: `radial-gradient(circle at 20% 50%, ${colorToRgba(primaryColor, 0.2)} 0%, transparent 50%), radial-gradient(circle at 80% 20%, ${colorToRgba(primaryColor, 0.15)} 0%, transparent 50%), radial-gradient(circle at 40% 80%, ${colorToRgba(primaryColor, 0.1)} 0%, transparent 50%)`,
          animation: 'float 20s ease-in-out infinite',
        }}
      />

      {/* Main content container */}
      <div 
        style={{
          background: prefersDarkMode 
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(255, 255, 255, 0.25)',
          backdropFilter: 'blur(20px)',
          border: prefersDarkMode 
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid rgba(255, 255, 255, 0.3)',
          borderRadius: '24px',
          padding: '60px',
          maxWidth: '520px',
          width: '90%',
          textAlign: 'center',
          position: 'relative',
          boxShadow: prefersDarkMode
            ? '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)'
            : '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)',
          animation: 'slideUp 0.8s ease-out',
        }}
      >
        {/* Logo container with glow effect */}
        <div 
          style={{
            marginBottom: '32px',
            position: 'relative',
            display: 'inline-block',
          }}
        >
                     <div
             style={{
               position: 'absolute',
               top: '50%',
               left: '50%',
               transform: 'translate(-50%, -50%)',
               width: '120px',
               height: '120px',
               background: `linear-gradient(45deg, ${colorToRgba(primaryColor, 0.4)}, ${colorToRgba(primaryColor, 0.6)})`,
               borderRadius: '50%',
               opacity: 0.3,
               filter: 'blur(20px)',
               animation: 'glow 3s ease-in-out infinite alternate',
             }}
           />
          <img 
            src={theme.logo} 
            alt={theme.name}
            style={{
              width: '80px',
              height: '80px',
              objectFit: 'contain',
              borderRadius: '16px',
              position: 'relative',
              zIndex: 1,
              background: 'rgba(255, 255, 255, 0.1)',
              padding: '12px',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
            }}
          />
        </div>

        {/* Brand name */}
        <h1 
          style={{
            fontSize: '42px',
            fontWeight: '700',
            margin: '0 0 16px 0',
            color: prefersDarkMode ? '#ffffff' : '#ffffff',
            fontFamily: theme.fonts?.heading || theme.fonts?.body || 'ui-sans-serif, system-ui, -apple-system, sans-serif',
            textShadow: '0 2px 20px rgba(0, 0, 0, 0.3)',
            lineHeight: '1.2',
          }}
        >
          {theme.name}
        </h1>

        {/* Access denied message */}
        <div style={{ marginBottom: '40px' }}>
          <p 
            style={{
              fontSize: '16px',
              lineHeight: '1.6',
              color: prefersDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.8)',
              margin: '0',
              maxWidth: '400px',
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          >
            You don't have the required plan or permissions to access this workspace. 
            Contact us to learn about our available plans and get started.
          </p>
        </div>

        {/* Action buttons */}
        <div 
          style={{
            display: 'flex',
            gap: '16px',
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
                     {/* Contact button */}
           <button
             onClick={handleContact}
             style={{
               background: 'rgba(255, 255, 255, 0.95)',
               color: primaryColor,
               border: `2px solid ${colorToRgba(primaryColor, 0.3)}`,
               borderRadius: '12px',
               padding: '16px 32px',
               fontSize: '16px',
               fontWeight: '700',
               cursor: 'pointer',
               transition: 'all 0.2s ease',
               position: 'relative',
               overflow: 'hidden',
               boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)',
               minWidth: '140px',
               fontFamily: 'inherit',
               backdropFilter: 'blur(10px)',
             }}
             onMouseEnter={(e) => {
               e.target.style.transform = 'translateY(-2px)';
               e.target.style.background = '#ffffff';
               e.target.style.boxShadow = '0 12px 35px rgba(0, 0, 0, 0.25)';
               e.target.style.borderColor = primaryColor;
             }}
             onMouseLeave={(e) => {
               e.target.style.transform = 'translateY(0)';
               e.target.style.background = 'rgba(255, 255, 255, 0.95)';
               e.target.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.2)';
               e.target.style.borderColor = colorToRgba(primaryColor, 0.3);
             }}
           >
             Contact Us
           </button>

          {/* Logout button */}
          <button
            onClick={handleLogout}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              color: prefersDarkMode ? 'rgba(255, 255, 255, 0.9)' : '#ffffff',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '12px',
              padding: '16px 32px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              backdropFilter: 'blur(10px)',
              minWidth: '140px',
              fontFamily: 'inherit',
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.2)';
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.1)';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            Sign Out
          </button>
        </div>

                 {/* Decorative elements */}
         <div 
           style={{
             position: 'absolute',
             top: '-20px',
             right: '-20px',
             width: '40px',
             height: '40px',
             background: `linear-gradient(45deg, ${colorToRgba(primaryColor, 0.2)}, ${colorToRgba(primaryColor, 0.1)})`,
             borderRadius: '50%',
             animation: 'float 4s ease-in-out infinite',
             boxShadow: `0 4px 15px ${colorToRgba(primaryColor, 0.2)}`,
           }}
         />
         <div 
           style={{
             position: 'absolute',
             bottom: '-15px',
             left: '-15px',
             width: '30px',
             height: '30px',
             background: `linear-gradient(45deg, ${colorToRgba(primaryColor, 0.15)}, ${colorToRgba(primaryColor, 0.08)})`,
             borderRadius: '50%',
             animation: 'float 6s ease-in-out infinite reverse',
             boxShadow: `0 3px 12px ${colorToRgba(primaryColor, 0.15)}`,
           }}
         />
      </div>

      {/* Global animations */}
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
          
          @keyframes slideUp {
            from {
              opacity: 0;
              transform: translateY(40px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes float {
            0%, 100% {
              transform: translateY(0px) rotate(0deg);
            }
            50% {
              transform: translateY(-20px) rotate(10deg);
            }
          }
          
          @keyframes glow {
            from {
              opacity: 0.2;
              transform: translate(-50%, -50%) scale(1);
            }
            to {
              opacity: 0.4;
              transform: translate(-50%, -50%) scale(1.1);
            }
          }
        `}
      </style>
    </div>
  );
}; 